// @ts-nocheck
// @ts-nocheck
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { 
  Globe, 
  Activity, 
  Server, 
  Users, 
  ArrowUpRight, 
  ArrowDownRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Database,
  HardDrive,
  Cpu,
  Network
} from 'lucide-react'
import { apiService } from '../services/api'
import { authService } from '../services/auth'
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts'

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('24h')
  const isAdmin = authService.isAdmin()

  // Fetch data
  const { data: zonesData, isLoading: zonesLoading } = useQuery('zonesList', 
    () => apiService.getZones(),
    { refetchInterval: 60000 }
  )

  const { data: networkSummary, isLoading: networkLoading } = useQuery('networkSummary',
    () => apiService.getNetworkSummary(),
    { refetchInterval: 30000 }
  )

  const { data: dnsStatus, isLoading: dnsLoading } = useQuery('dnsStatus',
    () => apiService.getDnsStatus(),
    { refetchInterval: 30000 }
  )

  const { data: systemInfo, isLoading: systemLoading } = useQuery('systemInfo',
    () => apiService.getSystemInfo(),
    { refetchInterval: 60000 }
  )

  const { data: alerts } = useQuery('alerts',
    () => apiService.getAlerts(false, 5),
    { refetchInterval: 60000 }
  )

  // Real network traffic data
  const { data: networkTraffic } = useQuery('networkTraffic',
    () => apiService.client.get('/monitoring?action=network-traffic').then(r => r.data?.data),
    { refetchInterval: 30000 }
  )

  // Real record types data
  const { data: recordTypesData } = useQuery('recordTypes',
    () => apiService.client.get('/monitoring?action=record-types').then(r => r.data?.data),
    { refetchInterval: 60000 }
  )

  // Real DNS traffic stats
  const { data: dnsTrafficStats, isLoading: trafficLoading } = useQuery('dnsTraffic',
    () => apiService.getDnsTrafficStats(),
    { refetchInterval: 30000 }
  )

  const interfaces = networkSummary?.data?.interfaces || []
  const totalInbound = interfaces.reduce((sum: number, item: any) => sum + Number(item.rx_mb || 0), 0)
  const totalOutbound = interfaces.reduce((sum: number, item: any) => sum + Number(item.tx_mb || 0), 0)
  const trafficData = interfaces.length > 0
    ? interfaces.map((item: any) => ({
        time: item.name,
        inbound: Number(item.rx_mb || 0),
        outbound: Number(item.tx_mb || 0),
      }))
    : [
        {
          time: 'Current',
          inbound: Number(networkTraffic?.inbound_mb || 0),
          outbound: Number(networkTraffic?.outbound_mb || 0),
        },
      ]

  const recordTypeData = recordTypesData ? [
    { name: 'A', value: recordTypesData.A || 0, color: '#0ea5e9' },
    { name: 'AAAA', value: recordTypesData.AAAA || 0, color: '#22c55e' },
    { name: 'MX', value: recordTypesData.MX || 0, color: '#f59e0b' },
    { name: 'CNAME', value: recordTypesData.CNAME || 0, color: '#8b5cf6' },
    { name: 'TXT', value: recordTypesData.TXT || 0, color: '#ef4444' },
    { name: 'NS', value: recordTypesData.NS || 0, color: '#ec4899' },
  ].filter(r => r.value > 0) : []

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    trend,
    trendUp,
    color = 'blue',
    loading = false 
  }: any) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      amber: 'bg-amber-50 text-amber-600',
      red: 'bg-red-50 text-red-600',
      purple: 'bg-purple-50 text-purple-600',
    }

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            {loading ? (
              <div className="mt-2 h-8 w-24 bg-slate-200 rounded animate-pulse" />
            ) : (
              <h3 className="text-2xl font-bold text-slate-900 mt-1">{value}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
            {trend && (
              <div className={`flex items-center mt-2 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
                {trendUp ? (
                  <ArrowUpRight className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 mr-1" />
                )}
                <span>{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color as keyof typeof colorClasses]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500">Overview of your DNS infrastructure and system health</p>
        </div>
        <div className="flex items-center space-x-3">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="1h">Last Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 transition-colors">
            Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {alerts?.data && alerts.data.length > 0 && (
        <div className="space-y-2">
          {alerts.data.slice(0, 3).map((alert: any) => (
            <div 
              key={alert.id}
              className={`p-4 rounded-lg flex items-center space-x-3 ${
                alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                alert.severity === 'warning' ? 'bg-amber-50 border border-amber-200' :
                'bg-blue-50 border border-blue-200'
              }`}
            >
              <AlertCircle className={`h-5 w-5 ${
                alert.severity === 'critical' ? 'text-red-500' :
                alert.severity === 'warning' ? 'text-amber-500' :
                'text-blue-500'
              }`} />
              <div className="flex-1">
                <p className={`font-medium ${
                  alert.severity === 'critical' ? 'text-red-800' :
                  alert.severity === 'warning' ? 'text-amber-800' :
                  'text-blue-800'
                }`}>
                  {alert.title}
                </p>
                <p className="text-sm text-slate-600">{alert.message}</p>
              </div>
              <span className="text-sm text-slate-500">{alert.created_at}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="DNS Zones"
          value={zonesData?.data?.length || 0}
          subtitle={`${zonesData?.data?.filter((z: any) => z.type === 'forward').length || 0} forward`}
          icon={Globe}
          color="blue"
          loading={zonesLoading}
        />
        <StatCard
          title="Zone Files"
          value={zonesData?.data?.length || 0}
          subtitle={`${zonesData?.data?.filter((z: any) => z.type === 'reverse').length || 0} reverse`}
          icon={Database}
          color="purple"
          loading={zonesLoading}
        />
        <StatCard
          title="Network Interfaces"
          value={systemInfo?.data?.hostname ? 1 : 0}
          subtitle={systemInfo?.data?.hostname || 'N/A'}
          icon={Network}
          color="green"
          loading={systemLoading}
        />
        <StatCard
          title="DNS Server"
          value={dnsStatus?.data?.status === 'running' ? 'Running' : 'Stopped'}
          subtitle={dnsStatus?.data?.version || 'Unknown version'}
          icon={Server}
          color={dnsStatus?.data?.status === 'running' ? 'green' : 'red'}
          loading={dnsLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Traffic Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Network Traffic</h3>
              <p className="text-sm text-slate-500">Inbound and outbound traffic over time</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="h-3 w-3 rounded-full bg-sky-500"></span>
                <span className="text-sm text-slate-600">Inbound</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="h-3 w-3 rounded-full bg-green-500"></span>
                <span className="text-sm text-slate-600">Outbound</span>
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trafficData}>
                <defs>
                  <linearGradient id="colorInbound" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorOutbound" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="time" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="inbound" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="outbound" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium uppercase text-slate-500">Inbound</p>
              <p className="text-lg font-semibold text-slate-900">{totalInbound.toFixed(2)} MB</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium uppercase text-slate-500">Outbound</p>
              <p className="text-lg font-semibold text-slate-900">{totalOutbound.toFixed(2)} MB</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium uppercase text-slate-500">Interfaces</p>
              <p className="text-lg font-semibold text-slate-900">{interfaces.length}</p>
            </div>
          </div>
        </div>

        {/* Record Types */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Record Types</h3>
          <p className="text-sm text-slate-500 mb-6">Distribution by type</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={recordTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {recordTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {recordTypeData.map((type) => (
              <div key={type.name} className="flex items-center space-x-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: type.color }}></span>
                <span className="text-sm text-slate-600">{type.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live DNS Traffic */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Live DNS Traffic</h3>
            <p className="text-sm text-slate-500">Recent query activity from DNS logs</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">{dnsTrafficStats?.data?.queries_per_minute || 0}</p>
            <p className="text-xs uppercase text-slate-500">queries/min</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 overflow-hidden border rounded-lg">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Client</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Domain</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(dnsTrafficStats?.data?.recent_queries || []).slice(0, 8).map((query: any, idx: number) => (
                  <tr key={`${query.domain}-${idx}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-sm text-slate-500">{query.time}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{query.client}</td>
                    <td className="px-3 py-2 text-sm font-medium text-slate-900">{query.domain}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{query.type}</td>
                  </tr>
                ))}
                {!(dnsTrafficStats?.data?.recent_queries || []).length && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No recent query log entries found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="space-y-3">
            {(dnsTrafficStats?.data?.top_domains || []).slice(0, 6).map((item: any) => (
              <div key={item.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700 truncate">{item.name}</span>
                <span className="text-sm font-semibold text-slate-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">System Information</h3>
          {systemLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : systemInfo?.data ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Cpu className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-700">CPU Load</span>
                </div>
                <span className="font-medium text-slate-900">
                  {systemInfo.data.load_average?.[0]?.toFixed(2) || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Database className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-700">Memory</span>
                </div>
                <span className="font-medium text-slate-900">
                  {systemInfo.data.memory?.usage_percent || 0}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <HardDrive className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-700">Disk Usage</span>
                </div>
                <span className="font-medium text-slate-900">
                  {systemInfo.data.disk?.[0]?.usage_percent || 0}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-slate-400" />
                  <span className="text-slate-700">Uptime</span>
                </div>
                <span className="font-medium text-slate-900">
                  {systemInfo.data.uptime || 'N/A'}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        {/* DNS Server Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">DNS Server Status</h3>
          {dnsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : dnsStatus?.data ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-700">Service Status</span>
                <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                  dnsStatus.data.running 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dnsStatus.data.running ? 'Running' : 'Stopped'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-700">Version</span>
                <span className="font-medium text-slate-900">
                  {dnsStatus.data.version || 'Unknown'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-700">Process ID</span>
                <span className="font-medium text-slate-900">
                  {dnsStatus.data.pid || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-700">Uptime</span>
                <span className="font-medium text-slate-900">
                  {dnsStatus.data.uptime || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-700">Configuration</span>
                <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                  dnsStatus.data.config_valid 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dnsStatus.data.config_valid ? 'Valid' : 'Invalid'}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
